import urlParser from 'url';

import React, { useEffect } from 'react';

import dynamic from 'next/dynamic';

import { useDispatch } from 'react-redux';

import Immutable from 'seamless-immutable';

import { CssBaseline, ThemeProvider as MUIThemeProvider } from '@material-ui/core';

import { ConfigCatProvider, createConsoleLogger, LogLevel } from 'configcat-react';
import isEmpty from 'lodash/isEmpty';
import { DefaultSeo } from 'next-seo';
import withError from 'next-with-error';
import { any, array, bool, object, string } from 'prop-types';
import { ThemeProvider } from 'styled-components';
// SEO

import createTheme from '../src/configuration/createMUITheme';
import GlobalStyles from '../src/configuration/globalStyles';
import { GtmScript, loadGtmNoScript } from '../src/configuration/GtmScripts';
import LinkTags from '../src/configuration/LinkTags';
import loadScripts from '../src/configuration/loadScripts';
import { createSessionId } from '../src/libs';
import { LazyLoadProvider } from '../src/libs/lazyLoadProvider';
import appAPI from '../src/redux/api/app';
import { Creators as AppCreators } from '../src/redux/reducers/app';
import { Creators as CustomerCreators } from '../src/redux/reducers/customer';
import { wrapper } from '../src/redux/store';
import { getCookie, setCookie } from '../src/redux/utils/cookies';
import { getServerUser } from '../src/redux/utils/cookiesUser';

const ErrorPage = dynamic(() => import('./_error'));
const Layout = dynamic(() => import('../src/containers/Layout'));
const CookiesPolicy = dynamic(() => import('../src/components/CookiesPolicy'), { ssr: false });

const SmartBrApp = ({
  Component,
  pageProps,
  error: { statusCode },
  theme,
  defaultSEO,
  gtmId,
  scripts,
  acceptsCookiesPolicy,
  showCookiesPolicy,
  campaign,
  featureFlag,
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(AppCreators.ssrRender(false));

    if (campaign.fbclid || campaign.gclid) {
      dispatch(AppCreators.sendCampaignRequest(campaign));
    }

    // if (process.env.NODE_ENV === 'production') {
    //   register();
    // }

    const jssStyles = document.querySelector('#jss-server-side');
    const nextScript = document.querySelector('#__NEXT_SCRIPTS_DEFER__');

    if (nextScript) {
      nextScript.parentElement.removeChild(nextScript);
    }

    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }

    loadScripts(scripts);
    loadGtmNoScript(gtmId, acceptsCookiesPolicy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <DefaultSeo {...defaultSEO} />
      <LinkTags />
      <GtmScript />
      <ConfigCatProvider
        sdkKey={featureFlag?.sdkKey || process.env.NEXT_PUBLIC_CONFIGCAT_SDK_KEY}
        options={{ pollIntervalSeconds: 60 * 5, logger: createConsoleLogger(LogLevel.Error) }}
      >
        <ThemeProvider theme={theme}>
          <MUIThemeProvider theme={createTheme({ ...theme.colors, ...theme?.template })}>
            <CssBaseline />
            <LazyLoadProvider>
              <Layout statusCode={statusCode}>
                {statusCode < 500 && <GlobalStyles />}
                {statusCode >= 200 && statusCode < 299 && showCookiesPolicy && <CookiesPolicy />}
                <Component {...pageProps} />
              </Layout>
            </LazyLoadProvider>
          </MUIThemeProvider>
        </ThemeProvider>
      </ConfigCatProvider>
    </>
  );
};

SmartBrApp.getInitialProps = async ({ Component, ctx }) => {
  const { req, res, pathname, asPath, query } = ctx;
  const showCookiesPolicy = getCookie(...[{}, { ctx, name: 'SMART_SHOW_COOKIES_POLICY' }]) !== 'false';
  // o usuário é somente AVISADO que os cookies estão sendo captados
  // const acceptsCookiesPolicy = getCookie(...[{}, { ctx, name: 'SMART_ACCEPT_COOKIES_POLICY' }]) === 'true';
  const acceptsCookiesPolicy = true;
  const serverCookies = getServerUser(ctx);

  const campaign = {
    gclid: query.gclid,
    fbclid: query.fbclid,
    path: urlParser.parse(req.url).pathname,
    param: urlParser.parse(req.url).query,
    ip: req.headers['x-real-ip'],
    ...serverCookies,
  };

  let smartPid = null;

  try {
    const { config = {}, ...otherConfigs } = await appAPI.initialStoreAndTheme({ origin: req.headers?.host });

    if (isEmpty(config)) {
      throw new Error('Aplicação não pode ser inicializada.');
    }

    const userAgent = req ? req.headers['user-agent'] : navigator.userAgent;
    const plataform = userAgent.toLowerCase().match(otherConfigs.mobileBrowsers) ? 'mobile' : 'desktop';

    ctx.store.dispatch(AppCreators.setCookiesPolicy({ show: showCookiesPolicy, accepted: acceptsCookiesPolicy }));
    ctx.store.dispatch(AppCreators.menuCategoryRequest(config.siteId));
    ctx.store.dispatch(
      AppCreators.initializeAppSuccess(
        Immutable({
          plataform,
          asPath,
          pathname,
          query,
          config: {
            ...config,
            ...otherConfigs,
          },
        })
      )
    );

    if (req && !isEmpty(serverCookies)) {
      if (serverCookies.id) serverCookies.signed = true;
      ctx.store.dispatch(CustomerCreators.initialCustomerSuccess(serverCookies));
    }

    if (!smartPid) {
      smartPid = createSessionId();
      setCookie('SMART_PID', smartPid, ctx, { domain: config.domain });
    }

    const data = await appAPI.fetchFeatureFlags({
      sdkKey: config.showComponents?.featureFlag?.sdkKey || '',
      userId: smartPid,
    });

    if (data) {
      const flags = data?.reduce((obj, flag) => {
        // eslint-disable-next-line no-param-reassign
        obj[flag.settingKey] = flag.settingValue;
        return obj;
      }, {});

      ctx.store.dispatch(AppCreators.featureFlags(flags));
    }
  } catch (err) {
    res.statusCode = 500;
  }

  const pageProps = {
    ...(Component.getInitialProps ? Component.getInitialProps(ctx) : {}),
    appProp: ctx.asPath,
  };

  const {
    app: {
      config: { theme, defaultSEO, gtmId, scripts, showComponents },
    },
  } = ctx.store.getState();

  return {
    pageProps,
    defaultSEO,
    theme,
    gtmId,
    scripts,
    campaign,
    showCookiesPolicy,
    acceptsCookiesPolicy,
    error: {
      statusCode: res.statusCode,
    },
    featureFlag: showComponents?.featureFlag,
  };
};

SmartBrApp.propTypes = {
  Component: any,
  pageProps: object,
  theme: object,
  showCookiesPolicy: bool,
  acceptsCookiesPolicy: bool,
  scripts: array,
  error: object,
  defaultSEO: object,
  gtmId: string,
  campaign: object,
  featureFlag: object,
};

export default wrapper.withRedux(withError(ErrorPage)(SmartBrApp));
